// ============================================================================
// MockTableTourPage - the tour's practice table (/tour/table)
// ============================================================================
// A table rendered entirely from static data: no Firestore, no server, no
// live players. The tour walks new users through it to explain reading a
// table - house bots, the action bar, and the table types - without the
// risks of parking a guided overlay on a live game (slow subscriptions,
// real stakes, other people's turns). Everything here is a prop, on purpose.
//
// Reached only by the tour (navigateTo: '/tour/table'); anyone who wanders
// in manually gets a note and a way home.
// ============================================================================
import React from 'react';
import { Box, HStack, VStack, Text, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { POKER_COLORS } from '../theme/designSystem';

const CARD_W = 44;

const PlayingCard: React.FC<{ rank?: string; suit?: string; back?: boolean }> = ({ rank, suit, back }) => (
  <Box
    w={`${CARD_W}px`} h={`${CARD_W * 1.4}px`} borderRadius="6px"
    bg={back ? 'linear-gradient(135deg, #7a1f2b, #4a1019)' : '#f8f8f4'}
    border="1px solid rgba(0,0,0,0.35)"
    display="flex" alignItems="center" justifyContent="center"
    boxShadow="0 3px 8px rgba(0,0,0,0.4)"
    fontWeight="800" fontSize="16px"
    color={suit === '♥' || suit === '♦' ? '#c62828' : '#1a1a1a'}
  >
    {back ? (
      <Box w="70%" h="80%" borderRadius="4px" border="2px solid rgba(255,255,255,0.25)" />
    ) : (
      <VStack spacing={0}><Text lineHeight="1">{rank}</Text><Text lineHeight="1">{suit}</Text></VStack>
    )}
  </Box>
);

const Seat: React.FC<{
  name: string; chips: string; isBot?: boolean; isYou?: boolean; avatar: string;
  cards?: Array<{ rank: string; suit: string }> | 'backs';
}> = ({ name, chips, isBot, isYou, avatar, cards }) => (
  <VStack spacing={1}>
    {cards === 'backs' && (
      <HStack spacing="-18px" mb={-2}>
        <PlayingCard back /><PlayingCard back />
      </HStack>
    )}
    {Array.isArray(cards) && (
      <HStack spacing="-10px" mb={-2}>
        {cards.map((c, i) => <PlayingCard key={i} rank={c.rank} suit={c.suit} />)}
      </HStack>
    )}
    <Box position="relative">
      <Box
        w="58px" h="58px" borderRadius="full" overflow="hidden"
        border="2.5px solid" borderColor={isYou ? POKER_COLORS.cyan : '#2d3748'}
        bg="#0A1F2E"
      >
        <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>
      {isBot && (
        <Box position="absolute" bottom="-4px" left="50%" transform="translateX(-50%)"
          bg="#0d3b2e" border="1px solid #22C55E" color="#4ade80"
          fontSize="8px" fontWeight="800" letterSpacing="0.1em"
          px={2} py="1px" borderRadius="8px">
          BOT
        </Box>
      )}
    </Box>
    <Box bg="rgba(3,9,14,0.85)" px={3} py="3px" borderRadius="10px" textAlign="center">
      <Text fontSize="11px" fontWeight="700" color={isYou ? 'cyan.300' : 'white'} lineHeight="1.2">{name}</Text>
      <Text fontSize="10px" color="#FFD700" lineHeight="1.2">{chips}</Text>
    </Box>
  </VStack>
);

const TABLE_TYPES = [
  { name: 'PlayCoins', color: POKER_COLORS.cyan, blurb: 'Free chips, real poker. Where everyone starts.' },
  { name: 'PSS Coins', color: '#FFD700', blurb: 'The sweepstakes tables - prizes ride on these.' },
  { name: 'Private', color: '#A855F7', blurb: 'Your table, your rules, your people. Share a code.' },
  { name: 'Tournament', color: '#22C55E', blurb: 'Scheduled events, prize pools, final tables.' },
];

const MockTableTourPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box minH="100vh" bg="#050f18" px={4} py={6} pb="120px">
      <VStack spacing={5} maxW="560px" mx="auto" align="stretch">
        <Box textAlign="center">
          <Text fontFamily="Georgia, serif" fontSize="22px" color="#E8F4F8">The Practice Felt</Text>
          <Text fontSize="12px" color="#7FA0B0">
            Nothing here is live — it's a guided look at how a table reads.
          </Text>
        </Box>

        {/* ── The felt ── */}
        <Box data-tutorial="mock-table-felt" position="relative"
          h="330px" borderRadius="160px"
          bg="radial-gradient(ellipse at 50% 42%, #0f3a30, #0a2620 72%)"
          border="9px solid #123246"
          boxShadow="inset 0 0 50px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.45)"
        >
          {/* bots across the top */}
          <Box position="absolute" top="-8px" left="18%" transform="translateX(-50%)">
            <Seat name="RoboRaiser" chips="1,840" isBot avatar="/avatars/pss/maverick_bust.svg" cards="backs" />
          </Box>
          <Box position="absolute" top="-8px" right="18%" transform="translateX(50%)"
            data-tutorial="mock-table-bots">
            <Seat name="LuckyLuna" chips="2,215" isBot avatar="/avatars/pss/ivy_bust.svg" cards="backs" />
          </Box>

          {/* board + pot */}
          <VStack position="absolute" top="46%" left="50%" transform="translate(-50%, -50%)" spacing={2}>
            <HStack spacing="6px">
              <PlayingCard rank="A" suit="♠" />
              <PlayingCard rank="J" suit="♥" />
              <PlayingCard rank="7" suit="♦" />
              <PlayingCard back /><PlayingCard back />
            </HStack>
            <Box bg="rgba(3,9,14,0.8)" border="1px solid #FFD700" borderRadius="14px" px={4} py="2px">
              <Text fontSize="12px" color="#FFD700" fontWeight="800">POT 460</Text>
            </Box>
          </VStack>

          {/* you at the bottom */}
          <Box position="absolute" bottom="-14px" left="50%" transform="translateX(-50%)">
            <Seat name="You" chips="2,000" isYou avatar="/avatars/pss/ace_bust.svg"
              cards={[{ rank: 'A', suit: '♥' }, { rank: 'K', suit: '♥' }]} />
          </Box>
        </Box>

        {/* ── Action bar ── */}
        <HStack data-tutorial="mock-table-actions" spacing={2} justify="center" pt={3}>
          {[
            { label: 'FOLD', bg: '#7a3a1f' },
            { label: 'CALL 40', bg: '#1f7a3a' },
            { label: 'RAISE', bg: '#1f5c7a' },
            { label: 'ALL-IN', bg: '#7a1f2b' },
          ].map(b => (
            <Box key={b.label} bg={b.bg} color="white" fontWeight="800" fontSize="13px"
              px={5} py={3} borderRadius="10px" opacity={0.95} textAlign="center"
              border="1px solid rgba(255,255,255,0.18)">
              {b.label}
            </Box>
          ))}
        </HStack>

        {/* ── Table types ── */}
        <Box data-tutorial="mock-table-types">
          <Text fontSize="10px" letterSpacing="0.18em" color="#47646F" textTransform="uppercase"
            textAlign="center" mb={2}>
            Four kinds of table
          </Text>
          <VStack spacing={2} align="stretch">
            {TABLE_TYPES.map(t => (
              <HStack key={t.name} bg="#0A1F2E" border="1px solid #16344a" borderRadius="12px" px={4} py={2.5}>
                <Box w="10px" h="10px" borderRadius="full" bg={t.color} flexShrink={0} />
                <Text fontSize="13px" fontWeight="700" color="white" minW="88px">{t.name}</Text>
                <Text fontSize="12px" color="#7FA0B0">{t.blurb}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Escape hatch for anyone who lands here outside the tour */}
        <Button variant="outline" colorScheme="cyan" size="sm" alignSelf="center"
          onClick={() => navigate('/')}>
          Back to the lobby
        </Button>
      </VStack>
    </Box>
  );
};

export default MockTableTourPage;
